import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Phone, Video, MessageSquare, BookOpen, Plus, Clock, ChevronRight } from '../components/CustomIcons';

export default function Connect() {
  const { user, authFetch } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const profile = user?.profile || {};

  useEffect(() => {
    authFetch('/connect/contacts').then(r => setContacts(r.data)).catch(() =>
      setContacts([
        { id: 'con-1', name: 'Maria Johnson', relationship: 'Mother', phone: '***-***-4821', last_contact: '2026-06-18' },
        { id: 'con-2', name: 'Keisha Johnson', relationship: 'Sister', phone: '***-***-7392', last_contact: '2026-06-15' },
        { id: 'con-3', name: 'Pastor James Wright', relationship: 'Mentor', phone: '***-***-5583', last_contact: '2026-06-19' },
        { id: 'con-4', name: 'Attorney David Park', relationship: 'Legal', phone: '***-***-2291', last_contact: '2026-06-10' },
      ])
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedContact) {
      authFetch(`/connect/messages/${selectedContact.id}`).then(r => setMessages(r.data)).catch(() =>
        setMessages([
          { id: '1', direction: 'sent', content: "Hey mom, I passed my electrical safety quiz today! 92%!", created_at: '2026-06-21T14:15:00Z' },
          { id: '2', direction: 'received', content: "I'm so proud of you baby! Keep going. We're all rooting for you.", created_at: '2026-06-21T14:32:00Z' },
          { id: '3', direction: 'sent', content: "They said I can start the NCCER cert prep next week. That's a real credential employers look for.", created_at: '2026-06-21T14:35:00Z' },
          { id: '4', direction: 'received', content: "That's amazing. Your uncle said his company hires people with that certification all the time.", created_at: '2026-06-21T14:48:00Z' },
        ])
      );
    }
  }, [selectedContact]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedContact || sending) return;
    setSending(true);
    const msg = { id: Date.now().toString(), direction: 'sent', content: newMessage, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, msg]);
    setNewMessage('');
    try { await authFetch(`/connect/messages/${selectedContact.id}/send`, { method: 'post', data: { content: msg.content } }); }
    catch (e) { console.error('send-message error:', e); }
    setSending(false);
  };

  const callMinutes = profile.call_minutes_today || 10;
  const chatMinutes = profile.chat_minutes_today || 15;

  const EARNING_RULES = [
    { action: 'Complete a lesson', reward: '+5 min' },
    { action: 'Pass a quiz', reward: '+10 min' },
    { action: '7-day streak', reward: '+15 bonus min' },
    { action: 'Earn a certification', reward: '+30 bonus min' },
  ];

  return (
    <div className="page-wrapper" style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="connect-page">
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--text-primary)', margin: '0 0 6px 0' }}>Stay Connected</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', fontFamily: 'Inter, sans-serif', margin: 0 }}>Earn call time through learning. Your progress keeps you connected.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '20px' }}>
          {/* Minutes Balance */}
          <div className="card" style={{ padding: '24px' }} data-testid="minutes-balance-card">
            <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', marginBottom: '16px' }}>Today's Allocation</h3>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '48px', color: '#F97316' }}>{callMinutes}</div>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>minutes available</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Base daily allocation</span>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>10 min</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Earned from learning</span>
                <span style={{ color: '#F97316', fontWeight: 600 }}>+22 min</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>7-day streak bonus</span>
                <span style={{ color: '#F97316', fontWeight: 600 }}>+15 min</span>
              </div>
            </div>
            <a href="/learn" style={{ display: 'block', textAlign: 'center', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: '10px', padding: '10px', color: '#F97316', fontSize: '14px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, textDecoration: 'none' }}>
              Earn More Minutes → Learn
            </a>
          </div>

          {/* Earning Rules */}
          <div className="card" style={{ padding: '24px' }}>
            <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', marginBottom: '16px' }}>How to Earn More</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {EARNING_RULES.map(r => (
                <div key={r.action} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', background: 'var(--bg-elevated)', borderRadius: '8px' }}>
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{r.action}</span>
                  <span style={{ fontSize: '14px', color: '#F97316', fontWeight: 600 }}>{r.reward}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '12px' }}>Minutes reset daily. Unused earned minutes carry over for 3 days.</p>
          </div>
        </div>

        {/* Messaging Interface */}
        <div className="card" style={{ padding: '0', overflow: 'hidden' }} data-testid="messaging-section">
          <div style={{ display: 'flex', height: '480px' }}>
            {/* Contact list */}
            <div style={{ width: '260px', borderRight: '1px solid #333333', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              <div style={{ padding: '16px', borderBottom: '1px solid var(--border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)', margin: 0 }}>Approved Contacts</h3>
                <button className="btn-secondary" style={{ fontSize: '11px', padding: '4px 8px' }}>
                  <Plus size={11} /> Request
                </button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {contacts.map(contact => (
                  <div key={contact.id} onClick={() => setSelectedContact(contact)}
                    style={{ padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid var(--border-subtle)', background: selectedContact?.id === contact.id ? 'rgba(249,115,22,0.08)' : 'transparent', borderLeft: selectedContact?.id === contact.id ? '3px solid #F97316' : '3px solid transparent' }}
                    data-testid={`contact-${contact.id}`}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#333333', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px', color: 'var(--text-secondary)' }}>
                        {contact.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 500, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{contact.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{contact.relationship}</div>
                      </div>
                    </div>
                    {selectedContact?.id !== contact.id && (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button className="btn-secondary" style={{ fontSize: '11px', padding: '4px 10px', flex: 1 }} onClick={e => e.stopPropagation()}><Phone size={11} /></button>
                        <button className="btn-secondary" style={{ fontSize: '11px', padding: '4px 10px', flex: 1 }} onClick={e => e.stopPropagation()}><MessageSquare size={11} /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Message thread */}
            {selectedContact ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', margin: 0 }}>{selectedContact.name}</h4>
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>{selectedContact.relationship}</p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn-secondary" style={{ fontSize: '12px', padding: '6px 12px' }}><Phone size={13} /> Call</button>
                    <button className="btn-secondary" style={{ fontSize: '12px', padding: '6px 12px' }}><Video size={13} /> Video</button>
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {messages.map(msg => (
                    <div key={msg.id} style={{ display: 'flex', justifyContent: msg.direction === 'sent' ? 'flex-end' : 'flex-start' }}>
                      <div style={{ maxWidth: '70%', padding: '10px 14px', borderRadius: '12px', background: msg.direction === 'sent' ? '#F97316' : '#2A2A2A', border: msg.direction === 'sent' ? 'none' : '1px solid #333333', color: msg.direction === 'sent' ? '#111111' : '#CCCCCC', fontSize: '14px', fontFamily: 'Inter, sans-serif', lineHeight: 1.5 }}>
                        {msg.content}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '12px 16px', borderTop: '1px solid #333333', display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '10px', padding: '8px 14px' }}>
                    <input value={newMessage} onChange={e => setNewMessage(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSend()}
                      placeholder="Type a message..." style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif', fontSize: '14px' }} data-testid="message-input" />
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{chatMinutes}m left</span>
                  </div>
                  <button onClick={handleSend} disabled={!newMessage.trim() || sending} className="btn-primary" style={{ padding: '10px 16px', fontSize: '14px' }} data-testid="send-message-btn">Send</button>
                </div>
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: 'var(--text-dim)' }}>
                <MessageSquare size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
                <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-secondary)' }}>Select a contact to start messaging</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
